import { Upload } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { Spinner } from '@/components/ui/Spinner';
import { cx } from '@/cva.config';
import { useUploadResume } from '@/hooks/useUploadResume';
import { RESUME_ACCEPT } from '@/lib/utils';

interface UploadStepProps {
  onNext: (resumeText: string, filePath: string, fileName: string) => void;
}

export const UploadStep = ({ onNext }: UploadStepProps) => {
  const upload = useUploadResume();

  const handleDrop = (acceptedFiles: File[]) => {
    const [file] = acceptedFiles;

    if (!file) {
      return;
    }

    upload.mutate(file, {
      onSuccess: (result) => {
        onNext(result.text, result.filePath, result.fileName);
      },
    });
  };

  const {
    getInputProps,
    getRootProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: RESUME_ACCEPT,
    disabled: upload.isPending,
    multiple: false,
    onDrop: handleDrop,
  });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg lg:text-xl font-semibold">
          Drop your resume in
        </h2>
        <p className="mt-1 text-sm lg:text-base text-foreground-muted">
          PDF, DOCX, Markdown, or plain text. The goblin parses it locally.
        </p>
      </div>

      <div
        {...getRootProps()}
        className={cx(
          'group flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 text-center transition-colors',
          isDragAccept && 'border-score-high bg-score-high/5',
          isDragReject && 'border-score-low bg-score-low/5',
          isDragActive
            && !isDragAccept
            && !isDragReject
            && 'border-accent bg-accent-soft/40',
          !isDragActive
            && 'border-border hover:border-accent/60 hover:bg-surface-sunken/40',
          upload.isPending && 'pointer-events-none opacity-60',
        )}
      >
        <input {...getInputProps()} />
        {upload.isPending ? (
          <div className="flex flex-col items-center gap-3">
            <Spinner label="" size="lg" />
            <p className="text-sm lg:text-base text-foreground-muted">
              Uploading and extracting...
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="size-7 text-foreground-muted group-hover:text-accent" />
            <p className="mt-1 text-sm lg:text-base text-foreground">
              Drop your resume here
            </p>
            <p className="font-mono text-2xs lg:text-sm text-subtle">
              or click to pick a file · pdf · docx · md · txt
            </p>
          </div>
        )}
      </div>

      {upload.isError && (
        <p className="text-center text-sm lg:text-base text-score-low">
          Upload failed: {upload.error.message}
        </p>
      )}
    </div>
  );
};
